import { History, Maximize2, Minimize2, PanelRightClose, Pin, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { ThreadDecisionFocus } from '@shared/types/thread-work';

/** Top of the side chat: new conversation, history, full screen, collapse; the pinned decision below. */
export const AppChatHeader = ({
  routeLabel,
  historyOpen,
  fullscreen,
  decisionFocus,
  onNewConversation,
  onToggleHistory,
  onToggleFullscreen,
  onCollapse,
  onClearDecisionFocus,
}: {
  routeLabel: string;
  historyOpen: boolean;
  fullscreen: boolean;
  decisionFocus: ThreadDecisionFocus | null;
  onNewConversation: () => void;
  onToggleHistory: () => void;
  onToggleFullscreen: () => void;
  onCollapse: () => void;
  onClearDecisionFocus: () => void;
}) => (
  <div className="border-b border-domino-border px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold text-domino-ink">Assistant</h2>
        <p className="mt-1 truncate text-xs text-domino-inkSoft">{routeLabel}</p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon" onClick={onNewConversation} title="Nouvelle conversation">
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleHistory}
          title="Historique"
          aria-pressed={historyOpen}
          className={cn(historyOpen && 'bg-domino-muted text-domino-aubergine')}
        >
          <History className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleFullscreen}
          title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          className="hidden md:inline-flex"
        >
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={onCollapse} title="Replier">
          <PanelRightClose className="h-4 w-4" />
        </Button>
      </div>
    </div>
    {decisionFocus && (
      <div className="mt-3 flex items-start justify-between gap-3 rounded-2xl border border-domino-prune/25 bg-domino-lilac/70 px-3 py-2.5">
        <div className="flex min-w-0 items-start gap-2 text-domino-aubergine">
          <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em]">
              Décision épinglée
            </p>
            <p className="mt-1 truncate text-xs font-semibold">
              {decisionFocus.accountLabel} · {decisionFocus.topicTitle}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClearDecisionFocus}
          aria-label="Retirer le focus sur cette décision"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-domino-aubergine transition hover:bg-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )}
  </div>
);
